import {
  CALENDAR_TYPE,
  CALENDAR_VIEW,
  type Calendar,
  type CalendarEventFormData,
  type CalendarEventOccurrence,
  type CalendarEventType,
  type CalendarEventsFilters,
  type CalendarSettings,
  EVENT_FREQUENCY,
  type EventExceptionView,
  type PartialDateTime,
} from '@/types';
import { EVENT_BY_DAY_PATTERN, MASS_TYPE } from '@/types/enums';
import {
  paletteColorCalendar,
  paletteColorCalendarBg,
  paletteColorCalendarBgHover,
  paletteColorCalendarText,
} from '@/styles/palette';
import dayjs, { endOfMonth, endOfWeek, endOfYear, format, startOfMonth, startOfWeek, startOfYear } from '@/utils/dayjs';
import { emptyCalendarEvent } from '@/constants/models';
import { FIRST_DAY_OF_WEEK_DEFAULT } from '@/constants/date';
import i18next from 'i18next';
import type { Dayjs } from 'dayjs';
import { normalizeEventDescription } from './calendarEventValidation';

const CALENDAR_EVENTS_API_VERSION = '2' as const;

export const isRecurringCalendarEvent = (event: CalendarEventType | null | undefined): boolean =>
  Boolean(event?.isRecurring || event?.eventRecurrence?.frequency);

export const getCalendarSeriesEventId = (event: CalendarEventType): string => {
  if (event.seriesEventId) {
    return event.seriesEventId;
  }

  if (event.isMaster || !event.parentEventId) {
    return event.id;
  }

  return event.parentEventId;
};

export const getCalendarOccurrenceDate = ({
  event,
  timezone,
}: {
  event: CalendarEventType;
  timezone?: string;
}): string | null => {
  if (event.occurrenceDate) {
    return event.occurrenceDate;
  }

  if (!event.startDate || event.isPerpetual) {
    return null;
  }

  if (event.isAllDay) {
    return dayjs.utc(event.startDate).format('YYYY-MM-DD');
  }

  if (timezone) {
    return dayjs.utc(event.startDate).tz(timezone).format('YYYY-MM-DD');
  }

  return dayjs(event.startDate).format('YYYY-MM-DD');
};

export const getCalendarEventLocationLabel = (event: CalendarEventType): string | null =>
  event.location?.value ?? event.locationLabel ?? null;

export const getFileNameFromUrl = (fileUrl: string | null | undefined): string | null => {
  if (!fileUrl) {
    return null;
  }

  try {
    const pathname = new URL(fileUrl).pathname;
    const segment = pathname.split('/').filter(Boolean).pop();
    return segment ? decodeURIComponent(segment) : null;
  } catch {
    const segment = fileUrl.split('/').filter(Boolean).pop();
    return segment ?? null;
  }
};

export const getCalendarEventStableId = (event: CalendarEventType): string =>
  event.occurrenceId ?? event.id;

/**
 * Maps a V2 list occurrence into the UI calendar event model without client-side expansion.
 */
export const mapCalendarEventOccurrence = ({
  occurrence,
  calendars = [],
  timezone,
}: {
  occurrence: CalendarEventOccurrence;
  calendars?: Calendar[];
  timezone?: string;
}): CalendarEventType => {
  const calendarFromList = occurrence.calendar?.id
    ? calendars.find((calendar) => calendar.id === occurrence.calendar.id)
    : undefined;
  const sourceColor = calendarFromList?.color ?? occurrence.calendar?.color;
  const { color, textColor, calendarColor, bgColorHover } = resolveCalendarEventColors({
    calendarColor: sourceColor,
  });

  const filePath = getFileNameFromUrl(occurrence.fileUrl);
  const isPerpetual = occurrence.isPerpetual;
  const isRecurring = occurrence.isRecurring;
  const seriesEventId = occurrence.seriesEventId;

  let convertedStartDate: Date | string | null = null;
  let convertedEndDate: Date | string | null = null;

  if (!isPerpetual && occurrence.startDate && occurrence.endDate && timezone) {
    convertedStartDate = dayjs.utc(occurrence.startDate).toDate();
    convertedEndDate = dayjs.utc(occurrence.endDate).toDate();
  } else if (!isPerpetual && occurrence.startDate && occurrence.endDate) {
    convertedStartDate = new Date(occurrence.startDate);
    convertedEndDate = new Date(occurrence.endDate);
  }

  const baseFields = {
    id: occurrence.occurrenceId,
    seriesEventId,
    occurrenceId: occurrence.occurrenceId,
    occurrenceDate: occurrence.occurrenceDate,
    overrideEventId: occurrence.overrideEventId,
    parentEventId: isRecurring ? seriesEventId : '',
    isMaster: !isRecurring,
    title: occurrence.title,
    description: normalizeEventDescription(occurrence.description),
    location: occurrence.location,
    locationLabel: occurrence.locationLabel,
    liturgy: occurrence.liturgy,
    language: occurrence.language,
    isAllDay: occurrence.isAllDay,
    allowRsvp: occurrence.allowRsvp,
    filePath,
    fileUrl: occurrence.fileUrl,
    color,
    textColor,
    calendarColor,
    bgColorHover,
    calendar: occurrence.calendar,
    isReadOnly: occurrence.isReadOnly,
    isRecurring,
    recurrenceDescription: occurrence.recurrenceDescription,
    churchCluster: occurrence.churchCluster,
    deactivatedAt: occurrence.deactivatedAt,
    startTime: null,
    endTime: null,
    eventRecurrence: null,
    eventExceptions: [] as CalendarEventType['eventExceptions'],
  };

  if (isPerpetual) {
    return {
      ...baseFields,
      isPerpetual: true as const,
      startDate: null,
      endDate: null,
    };
  }

  return {
    ...baseFields,
    isPerpetual: false as const,
    startDate: convertedStartDate ?? occurrence.startDate ?? '',
    endDate: convertedEndDate ?? occurrence.endDate ?? '',
  };
};

export const normalizeCalendarEventOccurrences = ({
  occurrences,
  calendars = [],
  timezone,
}: {
  occurrences: CalendarEventOccurrence[];
  calendars?: Calendar[];
  timezone?: string;
}): CalendarEventType[] => {
  const seenOccurrenceIds = new Set<string>();
  const events: CalendarEventType[] = [];

  occurrences.forEach((occurrence) => {
    if (seenOccurrenceIds.has(occurrence.occurrenceId)) {
      return;
    }

    seenOccurrenceIds.add(occurrence.occurrenceId);
    events.push(mapCalendarEventOccurrence({ occurrence, calendars, timezone }));
  });

  return events;
};

/**
 * Merges a list occurrence with the legacy series detail response used for edit forms.
 */
export const mergeOccurrenceWithSeriesDetail = ({
  occurrenceEvent,
  seriesDetail,
  calendars = [],
  timezone,
}: {
  occurrenceEvent: CalendarEventType;
  seriesDetail: CalendarEventType;
  calendars?: Calendar[];
  timezone?: string;
}): CalendarEventType => {
  const seriesEventId = getCalendarSeriesEventId(occurrenceEvent);
  const calendarFromList = occurrenceEvent.calendar?.id
    ? calendars.find((calendar) => calendar.id === occurrenceEvent.calendar.id)
    : undefined;
  const sourceColor = calendarFromList?.color ?? occurrenceEvent.calendar?.color ?? seriesDetail.calendar?.color;
  const colors = resolveCalendarEventColors({ calendarColor: sourceColor });

  const isPerpetual = occurrenceEvent.isPerpetual;
  const isRecurring = isRecurringCalendarEvent(occurrenceEvent) || Boolean(seriesDetail.eventRecurrence?.frequency);

  const baseFields = {
    ...seriesDetail,
    ...colors,
    id: seriesEventId,
    seriesEventId,
    occurrenceId: occurrenceEvent.occurrenceId ?? occurrenceEvent.id,
    occurrenceDate: occurrenceEvent.occurrenceDate ?? null,
    overrideEventId: occurrenceEvent.overrideEventId ?? null,
    parentEventId: isRecurring ? seriesEventId : '',
    isMaster: !isRecurring,
    title: occurrenceEvent.title,
    description: occurrenceEvent.description,
    location: occurrenceEvent.location,
    locationLabel: occurrenceEvent.locationLabel ?? null,
    liturgy: occurrenceEvent.liturgy,
    language: occurrenceEvent.language ?? seriesDetail.language,
    isAllDay: occurrenceEvent.isAllDay,
    allowRsvp: occurrenceEvent.allowRsvp,
    filePath: occurrenceEvent.filePath ?? seriesDetail.filePath,
    fileUrl: occurrenceEvent.fileUrl ?? seriesDetail.fileUrl,
    calendar: occurrenceEvent.calendar,
    isReadOnly: occurrenceEvent.isReadOnly,
    isRecurring,
    recurrenceDescription: occurrenceEvent.recurrenceDescription ?? seriesDetail.recurrenceDescription,
    churchCluster: occurrenceEvent.churchCluster ?? seriesDetail.churchCluster,
    eventRecurrence: seriesDetail.eventRecurrence,
    eventExceptions: seriesDetail.eventExceptions ?? [],
  };

  if (isPerpetual) {
    return {
      ...baseFields,
      isPerpetual: true as const,
      startDate: null,
      endDate: null,
    };
  }

  let startDate: Date | string = occurrenceEvent.startDate as Date | string;
  let endDate: Date | string = occurrenceEvent.endDate as Date | string;

  if (timezone && typeof occurrenceEvent.startDate === 'string') {
    startDate = dayjs.utc(occurrenceEvent.startDate).toDate();
  }
  if (timezone && typeof occurrenceEvent.endDate === 'string') {
    endDate = dayjs.utc(occurrenceEvent.endDate).toDate();
  }

  return {
    ...baseFields,
    isPerpetual: false as const,
    startDate,
    endDate,
  };
};

/**
 * Converts partial date/time objects to ISO string dates with timezone
 * @param start - Start date and time object
 * @param end - End date and time object (optional, returns null if not provided)
 * @param timezone - Timezone string (e.g., 'America/New_York')
 * @returns Object with startDate as ISO string and endDate as ISO string or null
 */
export const createEventDates = ({
  start,
  end,
  timezone,
}: {
  start: PartialDateTime;
  end?: PartialDateTime | null;
  timezone: string | undefined;
}): { startDate: string; endDate: string | null } => {
  if (!timezone) {
    throw new Error('Timezone is required');
  }

  const isStartAllDay = !start.time || start.time === '';

  const startDate = isStartAllDay
    ? dayjs.utc(start.date, 'YYYY-MM-DD').startOf('day').toISOString()
    : dayjs.tz(`${start.date} ${start.time}`, 'YYYY-MM-DD HH:mm', timezone).utc().toISOString();

  // Return null if end is null, undefined, or end.date is empty
  if (!end || !end.date || end.date.trim() === '') {
    return {
      startDate,
      endDate: null,
    };
  }

  const isEndAllDay = !end.time || end.time === '';
  const endDate = isEndAllDay
    ? dayjs.utc(end.date, 'YYYY-MM-DD').startOf('day').toISOString()
    : dayjs.tz(`${end.date} ${end.time}`, 'YYYY-MM-DD HH:mm', timezone).utc().toISOString();

  return {
    startDate,
    endDate,
  };
};

export const getAllDayEventDateRangeStrings = (
  startDate: Date | string,
  endDate: Date | string | null | undefined,
  timezone?: string
): { startDateStr: string; endDateStr: string } => {
  const startUtc = dayjs.utc(startDate);
  const startIsMidnightUtc =
    startUtc.hour() === 0 && startUtc.minute() === 0 && startUtc.second() === 0 && startUtc.millisecond() === 0;
  let startDateStr = startUtc.format('YYYY-MM-DD');
  if (!startIsMidnightUtc && timezone) {
    startDateStr = startUtc.tz(timezone).format('YYYY-MM-DD');
  }

  if (!endDate) {
    return { startDateStr, endDateStr: startDateStr };
  }

  const endUtc = dayjs.utc(endDate);
  const endIsMidnightUtc =
    endUtc.hour() === 0 && endUtc.minute() === 0 && endUtc.second() === 0 && endUtc.millisecond() === 0;
  const endIsEndOfDayUtc = endUtc.hour() === 23 && endUtc.minute() === 59 && endUtc.second() === 59;
  let endDateStr = endUtc.format('YYYY-MM-DD');
  if (!endIsMidnightUtc && !endIsEndOfDayUtc && timezone) {
    endDateStr = endUtc.tz(timezone).format('YYYY-MM-DD');
  }

  return { startDateStr, endDateStr };
};

export const getMonthlyNthWeekdayOption = (
  startDate: string | undefined | null
): { label: string; bySetPos: number; byDay: EVENT_BY_DAY_PATTERN[] } | null => {
  if (!startDate) return null;
  const d = dayjs(startDate);
  if (!d.isValid()) return null;

  const day = d.date();
  const idx = Math.floor((day - 1) / 7) + 1;
  const ord = ['first', 'second', 'third', 'fourth', 'fifth'][idx - 1];
  const weekdayName = d.format('dddd');
  if (!ord || !weekdayName) return null;

  const map = [
    EVENT_BY_DAY_PATTERN.SUNDAY,
    EVENT_BY_DAY_PATTERN.MONDAY,
    EVENT_BY_DAY_PATTERN.TUESDAY,
    EVENT_BY_DAY_PATTERN.WEDNESDAY,
    EVENT_BY_DAY_PATTERN.THURSDAY,
    EVENT_BY_DAY_PATTERN.FRIDAY,
    EVENT_BY_DAY_PATTERN.SATURDAY,
  ];
  const byDay = [map[d.day()]];

  return { label: `Monthly on the ${ord} ${weekdayName}`, bySetPos: idx, byDay };
};

export const getWeeklyOnWeekdayOption = (
  startDate: string | undefined | null
): { label: string; byDay: EVENT_BY_DAY_PATTERN[] } | null => {
  if (!startDate) return null;
  const d = dayjs(startDate);
  if (!d.isValid()) return null;
  const weekdayName = d.format('dddd');
  const map = [
    EVENT_BY_DAY_PATTERN.SUNDAY,
    EVENT_BY_DAY_PATTERN.MONDAY,
    EVENT_BY_DAY_PATTERN.TUESDAY,
    EVENT_BY_DAY_PATTERN.WEDNESDAY,
    EVENT_BY_DAY_PATTERN.THURSDAY,
    EVENT_BY_DAY_PATTERN.FRIDAY,
    EVENT_BY_DAY_PATTERN.SATURDAY,
  ];
  const byDay = [map[d.day()]];
  return { label: `Weekly on ${weekdayName}`, byDay };
};

export const getYearlyOnDateOption = (startDate: string | undefined | null): { label: string } | null => {
  if (!startDate) return null;
  const d = dayjs(startDate);
  if (!d.isValid()) return null;
  return { label: `Annually on ${d.format('MMMM D')}` };
};

type CalendarEventColors = {
  color: string;
  textColor: string;
  calendarColor: string;
  bgColorHover: string;
};

const DEFAULT_EVENT_COLORS: CalendarEventColors = {
  color: paletteColorCalendarBg[0],
  textColor: paletteColorCalendarText[0],
  calendarColor: paletteColorCalendar[0],
  bgColorHover: paletteColorCalendarBgHover[0],
};

const normalizeHexColor = (hex: string): string => {
  const cleaned = hex.trim().replace(/^#/, '');
  if (cleaned.length === 3) {
    return `#${cleaned
      .split('')
      .map((char) => `${char}${char}`)
      .join('')
      .toLowerCase()}`;
  }

  return `#${cleaned.toLowerCase()}`;
};

const mixHexWithWhite = ({ hex, colorWeight }: { hex: string; colorWeight: number }): string => {
  const normalized = normalizeHexColor(hex).slice(1);
  if (normalized.length !== 6) {
    return normalizeHexColor(hex);
  }

  const channels = [0, 2, 4].map((offset) => Number.parseInt(normalized.slice(offset, offset + 2), 16));
  if (channels.some((channel) => Number.isNaN(channel))) {
    return normalizeHexColor(hex);
  }

  const mixed = channels.map((channel) => Math.round(channel * colorWeight + 255 * (1 - colorWeight)));
  return `#${mixed.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
};

/**
 * Resolves event display colors from a calendar hex color.
 * Prefers the shared palette swatches; falls back to derived tints for legacy / out-of-palette colors.
 */
export const resolveCalendarEventColors = ({
  calendarColor,
}: {
  calendarColor?: string | null;
}): CalendarEventColors => {
  if (!calendarColor) {
    return DEFAULT_EVENT_COLORS;
  }

  const normalizedColor = normalizeHexColor(calendarColor);
  const colorIndex = paletteColorCalendar.findIndex(
    (paletteColor) => Boolean(paletteColor) && normalizeHexColor(paletteColor) === normalizedColor
  );

  if (colorIndex !== -1 && paletteColorCalendarBg[colorIndex]) {
    return {
      color: paletteColorCalendarBg[colorIndex],
      textColor: paletteColorCalendarText[colorIndex],
      calendarColor: paletteColorCalendar[colorIndex],
      bgColorHover: paletteColorCalendarBgHover[colorIndex],
    };
  }

  return {
    calendarColor: normalizedColor,
    color: mixHexWithWhite({ hex: normalizedColor, colorWeight: 0.12 }),
    bgColorHover: mixHexWithWhite({ hex: normalizedColor, colorWeight: 0.2 }),
    textColor: normalizedColor,
  };
};

/**
 * Converts date strings from API response to Date objects for CalendarEvent and adds calendar color
 * Expands recurring events into individual instances according to eventRecurrence
 * Converts UTC dates from server to church timezone
 * @param events - Array of calendar events with date strings
 * @param calendars - Array of calendars to map colors from
 * @param viewStartDate - Optional start date of the view to filter recurring instances
 * @param viewEndDate - Optional end date of the view to filter recurring instances
 * @param timezone - Timezone string (e.g., 'America/New_York') to convert UTC dates to
 * @returns Array of calendar events with Date objects and color, with recurring events expanded into instances
 */
export const transformCalendarEvents = (
  events: CalendarEventType[],
  calendars: Calendar[] = [],
  viewStartDate?: string,
  viewEndDate?: string,
  timezone?: string
): CalendarEventType[] => {
  const transformedEvents: CalendarEventType[] = [];
  events.forEach((event) => {
    const calendarFromList = event.calendar?.id
      ? calendars.find((cal) => cal.id === event.calendar.id)
      : undefined;
    // Prefer live calendar list (updated colors), fall back to embedded event.calendar from the API
    const sourceColor = calendarFromList?.color ?? event.calendar?.color;
    const { color, textColor, calendarColor, bgColorHover } = resolveCalendarEventColors({
      calendarColor: sourceColor,
    });
    const isPerpetual = event.isPerpetual;

    // Convert UTC dates from server to church timezone
    // Server sends UTC dates - we convert them to represent the same moment in church timezone
    let convertedStartDate: Date | string | null = null;
    let convertedEndDate: Date | string | null = null;

    if (!isPerpetual && event.startDate && event.endDate && timezone) {
      // Parse UTC date from server
      // Keep Date objects as UTC - they represent the same moment in time
      // Timezone conversion should be done when displaying/formatting dates, not when creating Date objects
      // JavaScript Date objects don't preserve timezone info, so we keep them as UTC
      const startDateUtc = dayjs.utc(event.startDate);
      const endDateUtc = dayjs.utc(event.endDate);

      // Keep as UTC Date objects - convert to timezone only when displaying
      convertedStartDate = startDateUtc.toDate();
      convertedEndDate = endDateUtc.toDate();
    } else if (!isPerpetual && event.startDate && event.endDate) {
      // If no timezone provided, just convert to Date objects
      convertedStartDate = typeof event.startDate === 'string' ? new Date(event.startDate) : event.startDate;
      convertedEndDate = typeof event.endDate === 'string' ? new Date(event.endDate) : event.endDate;
    }

    const eventWithColors: CalendarEventType = isPerpetual
      ? {
          ...event,
          isPerpetual: true as const,
          startDate: null,
          endDate: null,
          color,
          textColor,
          calendarColor,
          bgColorHover,
        }
      : {
          ...event,
          isPerpetual: false as const,
          startDate: convertedStartDate ?? event.startDate,
          endDate: convertedEndDate ?? event.endDate,
          color,
          textColor,
          calendarColor,
          bgColorHover,
        };

    // If event has recurrence pattern, mark as master and generate instances
    if (event.eventRecurrence?.frequency) {
      const recurrenceUntil = event.eventRecurrence.until ? dayjs(event.eventRecurrence.until) : null;
      const isMasterBeyondUntil =
        recurrenceUntil !== null &&
        Boolean(eventWithColors.startDate) &&
        dayjs(eventWithColors.startDate as Date | string).isAfter(recurrenceUntil, 'day');

      if (isMasterBeyondUntil) {
        return;
      }

      // Check if master event date has an overrideEvent - if so, don't add master event
      // The overrideEvent will be handled by generateRecurringEventInstances
      const masterEventDateStr = eventWithColors.startDate
        ? timezone
          ? dayjs.utc(eventWithColors.startDate).tz(timezone).format('YYYY-MM-DD')
          : dayjs(eventWithColors.startDate).format('YYYY-MM-DD')
        : null;
      const masterEventException = masterEventDateStr
        ? event.eventExceptions.find(
            (exception) =>
              exception.date &&
              formatExceptionDateKey(exception.date, timezone) === masterEventDateStr &&
              exception.overrideEvent
          )
        : null;

      // Only add master event if it fits into the selected date range AND doesn't have an overrideEvent
      // This prevents duplication when the master event date has an overrideEvent
      const shouldIncludeMaster =
        !masterEventException &&
        (!viewStartDate || !viewEndDate || !eventWithColors.startDate || !eventWithColors.endDate
          ? true // Include if no date range provided or event has no dates
          : (() => {
              const toEventDateStr = (date: Date | string) =>
                timezone ? dayjs.utc(date).tz(timezone).format('YYYY-MM-DD') : dayjs(date).format('YYYY-MM-DD');
              const eventStartStr = toEventDateStr(eventWithColors.startDate);
              const eventEndStr = toEventDateStr(eventWithColors.endDate);
              return eventStartStr <= viewEndDate && eventEndStr >= viewStartDate;
            })());

      if (shouldIncludeMaster) {
        const masterEvent: CalendarEventType & { isMaster?: boolean } = {
          ...eventWithColors,
          isMaster: true,
        };
        transformedEvents.push(masterEvent);
      }

      const instances = generateRecurringEventInstances(eventWithColors, viewStartDate, viewEndDate, timezone);
      transformedEvents.push(...instances);
    } else {
      transformedEvents.push(eventWithColors);
    }
  });

  return transformedEvents;
};

export const getCalendarEventsFilters = (calendarSettings: CalendarSettings): CalendarEventsFilters => {
  const { date, view } = calendarSettings;
  const weekStartsOn = calendarSettings.firstDayOfWeek ?? FIRST_DAY_OF_WEEK_DEFAULT;

  if (view === CALENDAR_VIEW.DAY) {
    const selectedDay = dayjs(date);
    return {
      version: CALENDAR_EVENTS_API_VERSION,
      startDate: format(selectedDay.startOf('day').toDate(), 'YYYY-MM-DD'),
      endDate: format(selectedDay.endOf('day').toDate(), 'YYYY-MM-DD'),
    };
  }

  if (view === CALENDAR_VIEW.WEEK) {
    const start = startOfWeek(date, { weekStartsOn });
    const end = endOfWeek(date, { weekStartsOn });
    return {
      version: CALENDAR_EVENTS_API_VERSION,
      startDate: format(start, 'YYYY-MM-DD'),
      endDate: format(end, 'YYYY-MM-DD'),
    };
  }

  if (view === CALENDAR_VIEW.MONTH) {
    // Match the visible month grid (includes spillover days from adjacent months)
    const monthStart = startOfMonth(date);
    const monthEnd = endOfMonth(date);
    const start = startOfWeek(monthStart, { weekStartsOn });
    const end = endOfWeek(monthEnd, { weekStartsOn });
    return {
      version: CALENDAR_EVENTS_API_VERSION,
      startDate: format(start, 'YYYY-MM-DD'),
      endDate: format(end, 'YYYY-MM-DD'),
    };
  }

  if (view === CALENDAR_VIEW.YEAR) {
    const start = startOfYear(date);
    const end = endOfYear(date);
    return {
      version: CALENDAR_EVENTS_API_VERSION,
      startDate: format(start, 'YYYY-MM-DD'),
      endDate: format(end, 'YYYY-MM-DD'),
    };
  }

  return {
    version: CALENDAR_EVENTS_API_VERSION,
    date: format(date, 'YYYY-MM-DD'),
  };
};

export const prepareEventForForm = (
  event: CalendarEventType | undefined | null,
  timezone?: string
): CalendarEventFormData => {
  if (event) {
    // Format dates in church timezone if provided, otherwise use browser local timezone
    const formatDate = (date: Date | string | null) => {
      if (!date) return '';
      if (event.isAllDay || event.isPerpetual) {
        return dayjs.utc(date).format('YYYY-MM-DD');
      }
      if (!timezone) return dayjs(date).format('YYYY-MM-DD');
      return dayjs.utc(date).tz(timezone).format('YYYY-MM-DD');
    };

    const formatTime = (date: Date | string | null) => {
      if (!date) return '';
      if (!timezone) {
        return dayjs(date).format('HH:mm');
      }
      return dayjs.utc(date).tz(timezone).format('HH:mm');
    };

    const { location, liturgy, language: _language, ...restEvent } = event;

    return {
      ...emptyCalendarEvent,
      ...restEvent,
      description: normalizeEventDescription(event.description),
      locationId: location?.id || null,
      liturgyId: liturgy?.id || null,
      eventChurchId: event.churchCluster?.churchId || '',
      calendarId: event.calendar.id,
      isPerpetual: event.isPerpetual ?? false,
      isAllDay: event.isPerpetual ? true : (event.isAllDay ?? false),
      start: {
        date: event.isPerpetual || !event.startDate ? '' : formatDate(event.startDate),
        time: event.isAllDay || event.isPerpetual || !event.startDate ? undefined : formatTime(event.startDate),
      },
      end:
        event.isPerpetual || !event.endDate
          ? null
          : {
              date: formatDate(event.endDate),
              time: event.isAllDay || event.isPerpetual ? undefined : formatTime(event.endDate),
            },
      formAttachment: {
        filePath: event.filePath,
        removeCurrentFile: false,
      },
    };
  }

  return emptyCalendarEvent;
};

/**
 * Parses time values to dayjs objects for comparison
 * @param timeValue - Time value as string (HH:mm or 12h format), Date object, or null/undefined
 * @returns Dayjs object or null if parsing fails
 */
export const parseTime = (timeValue: string | Date | null | undefined): Dayjs | null => {
  if (!timeValue) return null;
  if (timeValue instanceof Date) {
    return dayjs(timeValue);
  }
  if (typeof timeValue === 'string') {
    // If it's in HH:mm format, parse it with a dummy date
    if (/^\d{2}:\d{2}$/.test(timeValue)) {
      return dayjs(`2000-01-01 ${timeValue}`, 'YYYY-MM-DD HH:mm');
    }
    // Try parsing as 12h format (h:mm A or hh:mm A)
    const parsed12h = dayjs(timeValue, ['h:mm A', 'hh:mm A'], true);
    if (parsed12h.isValid()) {
      return parsed12h;
    }
    // Try parsing as HH:mm format
    const parsed24h = dayjs(timeValue, 'HH:mm', true);
    if (parsed24h.isValid()) {
      return dayjs(`2000-01-01 ${timeValue}`, 'YYYY-MM-DD HH:mm');
    }
  }
  return null;
};

/**
 * Validates that end time is not before start time on the same day
 * @param startTime - Start time value
 * @param endTime - End time value
 * @param startDate - Start date string
 * @param endDate - End date string
 * @returns Error message string or null if validation passes
 */
export const validateTimeOrder = (
  startTime: string | Date | null | undefined,
  endTime: string | Date | null | undefined,
  startDate: string,
  endDate: string
): string | null => {
  if (!startTime || !endTime || !startDate || !endDate) return null;
  if (startDate !== endDate) return null;

  const start = parseTime(startTime);
  const end = parseTime(endTime);

  if (start && end && end.isBefore(start, 'minute')) {
    return i18next.t('forms.calendarEvent.endTimeBeforeStartTimeError', { ns: 'global' });
  }

  return null;
};

/**
 * Shifts a time value one hour forward, preserving the 12h/24h notation of the input
 * @param timeValue - Time string such as "9:00 AM" or "09:00"
 * @returns Shifted time string, or null when the input cannot be parsed
 */
export const addOneHourToTime = (timeValue: string): string | null => {
  if (!timeValue) return null;
  const parsed = dayjs(timeValue, ['h:mm A', 'hh:mm A', 'H:mm', 'HH:mm'], true);
  if (!parsed.isValid()) return null;
  const next = parsed.add(1, 'hour');
  const isAmPm = /am|pm/i.test(timeValue);
  return isAmPm ? next.format('hh:mm A') : next.format('HH:mm');
};

type LiturgicalMassTimesType = MASS_TYPE.MASS | MASS_TYPE.ADORATION | MASS_TYPE.CONFESSION;

const LITURGICAL_MASS_TIMES_TYPES: ReadonlySet<string> = new Set([
  MASS_TYPE.MASS,
  MASS_TYPE.ADORATION,
  MASS_TYPE.CONFESSION,
]);

/**
 * True only for mass / confession / adoration. Treats null and "default" as regular calendars.
 */
export const isLiturgicalMassTimesType = (
  massTimesType: Calendar['massTimesType'] | undefined
): massTimesType is LiturgicalMassTimesType =>
  massTimesType != null && LITURGICAL_MASS_TIMES_TYPES.has(massTimesType);

export const isMassTimesCalendar = (calendar: Pick<Calendar, 'type' | 'massTimesType'>): boolean =>
  calendar.type === CALENDAR_TYPE.MASS_TIMES || isLiturgicalMassTimesType(calendar.massTimesType);

export const isMassCalendarEvent = ({ calendar }: Pick<CalendarEventType, 'calendar'>): boolean =>
  Boolean(calendar && isMassTimesCalendar(calendar));

/**
 * Mass times are announced as a single start time. Their end date only exists to give the
 * occurrence a duration, so it must never reach any surface that renders a time range.
 * @param event - Calendar event to resolve the displayable end date for
 * @returns The event end date, or null when the event belongs to a mass times calendar
 */
export const getEventDisplayEndDate = (event: CalendarEventType): Date | string | null =>
  isMassCalendarEvent(event) ? null : event.endDate;

export const formatExceptionDateKey = (date: Date | string, timezone?: string): string | null => {
  if (typeof date === 'string') {
    const dateOnlyPart = date.trim().split('T')[0]?.split(' ')[0] ?? '';

    // API exception dates are calendar dates (YYYY-MM-DD), not UTC instants
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnlyPart)) {
      if (timezone) {
        return dayjs.tz(dateOnlyPart, 'YYYY-MM-DD', timezone).format('YYYY-MM-DD');
      }
      return dateOnlyPart;
    }
  }

  const parsed = timezone ? dayjs.utc(date).tz(timezone) : dayjs(date);
  if (!parsed.isValid()) {
    return null;
  }
  return parsed.format('YYYY-MM-DD');
};

const buildOccurrenceUtcRange = ({
  occurrenceLocalDate,
  originalStartLocal,
  durationMs,
  timezone,
  isAllDay,
}: {
  occurrenceLocalDate: Dayjs;
  originalStartLocal: Dayjs;
  durationMs: number;
  timezone?: string;
  isAllDay: boolean;
}): { startDate: Date; endDate: Date } => {
  if (!timezone) {
    const instanceStartUtc = dayjs
      .utc(occurrenceLocalDate.format('YYYY-MM-DD'))
      .hour(originalStartLocal.hour())
      .minute(originalStartLocal.minute())
      .second(originalStartLocal.second())
      .millisecond(originalStartLocal.millisecond());
    return {
      startDate: instanceStartUtc.toDate(),
      endDate: instanceStartUtc.add(durationMs, 'millisecond').toDate(),
    };
  }

  const dateStr = occurrenceLocalDate.format('YYYY-MM-DD');
  const instanceStartUtc = isAllDay
    ? dayjs.tz(dateStr, 'YYYY-MM-DD', timezone).startOf('day')
    : dayjs
        .tz(`${dateStr} ${originalStartLocal.format('HH:mm:ss')}`, 'YYYY-MM-DD HH:mm:ss', timezone)
        .millisecond(originalStartLocal.millisecond());

  const startDate = instanceStartUtc.utc().toDate();
  const endDate = instanceStartUtc.add(durationMs, 'millisecond').utc().toDate();

  return { startDate, endDate };
};

/**
 * Generates recurring event instances based on recurrence frequency
 * Creates full CalendarEventType instances with adjusted dates
 * @param event - The recurring calendar event to generate instances from
 * @param viewStartDate - Optional start date of the view to filter instances (only generate instances that overlap with view)
 * @param viewEndDate - Optional end date of the view to filter instances (only generate instances that overlap with view)
 * @param timezone - Church timezone (e.g. 'America/New_York') to anchor wall-clock recurrence times across DST
 * @returns Array of CalendarEventType instances representing occurrences of the recurring event within the view range
 */
export const generateRecurringEventInstances = (
  event: CalendarEventType,
  viewStartDate?: string,
  viewEndDate?: string,
  timezone?: string
): CalendarEventType[] => {
  // Only process non-perpetual events with recurrence pattern
  if ((event as CalendarEventType).isPerpetual || !event.eventRecurrence?.frequency) {
    return [];
  }

  const recurrencePattern = event.eventRecurrence;
  const originalStartDate = timezone ? dayjs.utc(event.startDate).tz(timezone) : dayjs.utc(event.startDate);
  const originalEndDate = timezone ? dayjs.utc(event.endDate).tz(timezone) : dayjs.utc(event.endDate);

  if (!originalStartDate.isValid() || !originalEndDate.isValid()) {
    return [];
  }

  // Calculate the duration of the original event
  const durationMs = originalEndDate.diff(originalStartDate);

  const untilDatePart = recurrencePattern.until?.split('T')[0] ?? null;
  const until = untilDatePart
    ? timezone
      ? dayjs.tz(untilDatePart, 'YYYY-MM-DD', timezone).endOf('day')
      : dayjs(recurrencePattern.until)
    : null;
  if (until && !until.isValid()) {
    return [];
  }

  // Parse view date range for filtering
  const viewStart = viewStartDate
    ? timezone
      ? dayjs.tz(viewStartDate, 'YYYY-MM-DD', timezone).startOf('day')
      : dayjs.utc(viewStartDate).startOf('day')
    : null;
  const viewEnd = viewEndDate
    ? timezone
      ? dayjs.tz(viewEndDate, 'YYYY-MM-DD', timezone).endOf('day')
      : dayjs.utc(viewEndDate).endOf('day')
    : null;

  // Parse eventExceptions to a map of date strings to exception objects
  const exceptionMap = new Map<string, EventExceptionView>();
  event.eventExceptions.forEach((exception: EventExceptionView) => {
    if (exception.date) {
      const dateStr = formatExceptionDateKey(exception.date, timezone);
      if (dateStr) {
        exceptionMap.set(dateStr, exception);
      }
    }
  });

  // Check if original start date has an overrideEvent - if so, we need to process it
  const originalStartDateStr = originalStartDate.format('YYYY-MM-DD');
  const originalStartException = exceptionMap.get(originalStartDateStr);
  const hasOverrideEventForOriginalDate = !!originalStartException?.overrideEvent;

  if (recurrencePattern.frequency === EVENT_FREQUENCY.EVERY_WEEKDAY) {
    recurrencePattern.frequency = EVENT_FREQUENCY.WEEKLY;
    recurrencePattern.interval = 1;
    recurrencePattern.byDay = [
      EVENT_BY_DAY_PATTERN.MONDAY,
      EVENT_BY_DAY_PATTERN.TUESDAY,
      EVENT_BY_DAY_PATTERN.WEDNESDAY,
      EVENT_BY_DAY_PATTERN.THURSDAY,
      EVENT_BY_DAY_PATTERN.FRIDAY,
    ];
  }

  if (
    recurrencePattern.frequency === EVENT_FREQUENCY.WEEKLY &&
    Array.isArray(recurrencePattern.byDay) &&
    recurrencePattern.byDay.length > 0
  ) {
    const instances: CalendarEventType[] = [];
    const MAX_INSTANCES = 10000;
    let iterationCount = 0;
    const interval = Math.max(1, recurrencePattern.interval || 1);
    const allowed = new Set(recurrencePattern.byDay);
    const startWeek = originalStartDate.startOf('week');
    const weekdayMap: Record<number, EVENT_BY_DAY_PATTERN> = {
      0: EVENT_BY_DAY_PATTERN.SUNDAY,
      1: EVENT_BY_DAY_PATTERN.MONDAY,
      2: EVENT_BY_DAY_PATTERN.TUESDAY,
      3: EVENT_BY_DAY_PATTERN.WEDNESDAY,
      4: EVENT_BY_DAY_PATTERN.THURSDAY,
      5: EVENT_BY_DAY_PATTERN.FRIDAY,
      6: EVENT_BY_DAY_PATTERN.SATURDAY,
    };

    let cursor = originalStartDate.startOf('day');
    if (viewStart && cursor.isBefore(viewStart, 'day')) {
      cursor = viewStart.startOf('day').subtract(1, 'day');
      if (cursor.isBefore(originalStartDate, 'day')) {
        cursor = originalStartDate.startOf('day');
      }
    }

    while (iterationCount < MAX_INSTANCES) {
      if (until && cursor.isAfter(until, 'day')) {
        break;
      }
      if (viewEnd && cursor.isAfter(viewEnd, 'day')) {
        break;
      }

      const weeksDiff = cursor.startOf('week').diff(startWeek, 'week');
      const code = weekdayMap[cursor.day()];
      if (weeksDiff >= 0 && weeksDiff % interval === 0 && allowed.has(code)) {
        const occurrenceDate = cursor
          .hour(originalStartDate.hour())
          .minute(originalStartDate.minute())
          .second(originalStartDate.second())
          .millisecond(originalStartDate.millisecond());
        const instanceDateStr = occurrenceDate.format('YYYY-MM-DD');
        const exception = exceptionMap.get(instanceDateStr);

        if (
          occurrenceDate.isSame(originalStartDate, 'day') &&
          !exception?.overrideEvent &&
          !hasOverrideEventForOriginalDate
        ) {
          cursor = cursor.add(1, 'day');
          iterationCount++;
          continue;
        }

        if (exception?.isCancelled) {
          cursor = cursor.add(1, 'day');
          iterationCount++;
          continue;
        }

        if (exception?.overrideEvent) {
          const overrideInstance = {
            ...exception.overrideEvent,
            eventRecurrence: event.eventRecurrence,
            color: event.color,
            textColor: event.textColor,
            calendarColor: event.calendarColor,
            bgColorHover: event.bgColorHover,
            parentEventId: exception.parentEventId,
          };

          if (viewStart && viewEnd && overrideInstance.startDate && overrideInstance.endDate) {
            const overrideStart = timezone
              ? dayjs.utc(overrideInstance.startDate).tz(timezone)
              : dayjs(overrideInstance.startDate);
            const overrideEnd = timezone
              ? dayjs.utc(overrideInstance.endDate).tz(timezone)
              : dayjs(overrideInstance.endDate);
            if (overrideEnd.isBefore(viewStart) || overrideStart.isAfter(viewEnd)) {
              cursor = cursor.add(1, 'day');
              iterationCount++;
              continue;
            }
          }

          instances.push(overrideInstance);
        } else {
          const { startDate: instanceStartDate, endDate: instanceEndDate } = buildOccurrenceUtcRange({
            occurrenceLocalDate: occurrenceDate,
            originalStartLocal: originalStartDate,
            durationMs,
            timezone,
            isAllDay: event.isAllDay,
          });

          if (viewStart && viewEnd) {
            const instanceStart = timezone ? dayjs.utc(instanceStartDate).tz(timezone) : dayjs(instanceStartDate);
            const instanceEnd = timezone ? dayjs.utc(instanceEndDate).tz(timezone) : dayjs(instanceEndDate);
            if (instanceEnd.isBefore(viewStart) || instanceStart.isAfter(viewEnd)) {
              cursor = cursor.add(1, 'day');
              iterationCount++;
              continue;
            }
          }

          instances.push({
            ...event,
            isPerpetual: false as const,
            startDate: instanceStartDate,
            endDate: instanceEndDate,
            eventRecurrence: {
              frequency: recurrencePattern.frequency,
              interval: recurrencePattern.interval,
              byDay: recurrencePattern.byDay,
              bySetPos: recurrencePattern.bySetPos ?? null,
              until: recurrencePattern.until,
              count: recurrencePattern.count,
            },
            eventExceptions: [],
            parentEventId: event.id,
          });
        }
      }

      cursor = cursor.add(1, 'day');
      iterationCount++;
    }

    return instances;
  }

  const instances: CalendarEventType[] = [];
  // Start from the original start date, preserving time component
  let current = originalStartDate;

  // If view range is provided, optimize by starting from viewStart or originalStartDate (whichever is later)
  // BUT: if original start date has an overrideEvent that overlaps with view range, we need to process it
  if (viewStart && current.isBefore(viewStart)) {
    // Check if original start date has overrideEvent that overlaps with view range
    let shouldProcessOriginalDateOverride = false;
    if (hasOverrideEventForOriginalDate && viewEnd && originalStartException?.overrideEvent) {
      const overrideEvent = originalStartException.overrideEvent;
      if (overrideEvent && overrideEvent.startDate && overrideEvent.endDate) {
        const overrideStart = dayjs(overrideEvent.startDate);
        const overrideEnd = dayjs(overrideEvent.endDate);
        // Check if overrideEvent overlaps with view range
        if (!(overrideEnd.isBefore(viewStart) || overrideStart.isAfter(viewEnd))) {
          shouldProcessOriginalDateOverride = true;
        }
      }
    }

    // Only advance if we don't need to process the overrideEvent for original date
    if (!shouldProcessOriginalDateOverride) {
      // Calculate how many iterations we need to skip to reach the view start
      // This is an optimization to avoid generating instances before the view range
      const daysDiff = viewStart.diff(current, 'day');
      switch (recurrencePattern.frequency) {
        case EVENT_FREQUENCY.DAILY:
          current = current.add(Math.floor(daysDiff), 'day');
          break;
        case EVENT_FREQUENCY.WEEKLY:
          current = current.add(Math.floor(daysDiff / 7), 'week');
          break;
        case EVENT_FREQUENCY.MONTHLY:
          current = current.add(Math.floor(daysDiff / 30), 'month');
          break;
        case EVENT_FREQUENCY.YEARLY:
          current = current.add(Math.floor(daysDiff / 365), 'year');
          break;
      }
      // Ensure we don't skip past the view start
      while (current.isBefore(viewStart) && current.isBefore(until || dayjs('2099-12-31'))) {
        switch (recurrencePattern.frequency) {
          case EVENT_FREQUENCY.DAILY:
            current = current.add(1, 'day');
            break;
          case EVENT_FREQUENCY.WEEKLY:
            current = current.add(1, 'week');
            break;
          case EVENT_FREQUENCY.MONTHLY:
            current = current.add(1, 'month');
            break;
          case EVENT_FREQUENCY.YEARLY:
            current = current.add(1, 'year');
            break;
        }
      }
    }
    // If shouldProcessOriginalDateOverride is true, current stays at originalStartDate
  }

  // Skip the first occurrence (master event) to avoid duplication
  // BUT: if there's an overrideEvent for the original date, we should NOT skip it
  // Only skip if current still equals originalStartDate AND there's no overrideEvent
  if (current.isSame(originalStartDate, 'day') && !hasOverrideEventForOriginalDate) {
    switch (recurrencePattern.frequency) {
      case EVENT_FREQUENCY.DAILY:
        current = current.add(1, 'day');
        break;
      case EVENT_FREQUENCY.WEEKLY:
        current = current.add(1, 'week');
        break;
      case EVENT_FREQUENCY.MONTHLY:
        current = current.add(1, 'month');
        break;
      case EVENT_FREQUENCY.YEARLY:
        current = current.add(1, 'year');
        break;
    }
  }

  // Safety limit to prevent infinite loops (max 10,000 instances)
  const MAX_INSTANCES = 10000;
  let iterationCount = 0;

  while (iterationCount < MAX_INSTANCES) {
    // Stop if we've passed the until date (compare by day only)
    if (until && current.isAfter(until, 'day')) {
      break;
    }

    // Stop if we've passed the view end date (if provided)
    if (viewEnd && current.isAfter(viewEnd, 'day')) {
      break;
    }

    let occurrenceDate = current;
    if (
      recurrencePattern.frequency === EVENT_FREQUENCY.MONTHLY &&
      recurrencePattern.bySetPos &&
      Array.isArray(recurrencePattern.byDay) &&
      recurrencePattern.byDay.length === 1
    ) {
      const code = recurrencePattern.byDay[0];
      const map: Record<string, number> = { SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6 };
      const w = map[code] ?? null;
      if (w !== null) {
        const firstOfMonth = current.startOf('month');
        const offset = (w - firstOfMonth.day() + 7) % 7;
        const dateNum = 1 + offset + 7 * (recurrencePattern.bySetPos - 1);
        if (dateNum <= firstOfMonth.daysInMonth()) {
          occurrenceDate = firstOfMonth
            .date(dateNum)
            .hour(originalStartDate.hour())
            .minute(originalStartDate.minute())
            .second(originalStartDate.second())
            .millisecond(originalStartDate.millisecond());
        } else {
          switch (recurrencePattern.frequency) {
            case EVENT_FREQUENCY.MONTHLY:
              current = current.add(1, 'month');
              break;
            default:
              break;
          }
          iterationCount++;
          continue;
        }
      }
    }
    const instanceDateStr = occurrenceDate.format('YYYY-MM-DD');
    const exception = exceptionMap.get(instanceDateStr);

    // Skip the original start date (master event) to avoid duplication
    // BUT: if there's an overrideEvent for this date, we should process it instead
    // This check ensures we skip even if view range optimization advanced current
    if (current.isSame(originalStartDate, 'day') && !exception?.overrideEvent) {
      switch (recurrencePattern.frequency) {
        case EVENT_FREQUENCY.DAILY:
          current = current.add(1, 'day');
          break;
        case EVENT_FREQUENCY.WEEKLY:
          current = current.add(1, 'week');
          break;
        case EVENT_FREQUENCY.MONTHLY:
          current = current.add(1, 'month');
          break;
        case EVENT_FREQUENCY.YEARLY:
          current = current.add(1, 'year');
          break;
        default:
          return instances;
      }
      iterationCount++;
      continue;
    }

    // Skip if this date has an exception with isCancelled: true
    if (exception?.isCancelled) {
      // Skip this occurrence - it's cancelled
      switch (recurrencePattern.frequency) {
        case EVENT_FREQUENCY.DAILY:
          current = current.add(1, 'day');
          break;
        case EVENT_FREQUENCY.WEEKLY:
          current = current.add(1, 'week');
          break;
        case EVENT_FREQUENCY.MONTHLY:
          current = current.add(1, 'month');
          break;
        case EVENT_FREQUENCY.YEARLY:
          current = current.add(1, 'year');
          break;
        default:
          return instances;
      }
      iterationCount++;
      continue;
    }

    // If there's an overrideEvent for this date, use it as the instance
    if (exception?.overrideEvent) {
      const overrideInstance = {
        ...exception.overrideEvent,
        eventRecurrence: event.eventRecurrence,
        color: event.color,
        textColor: event.textColor,
        calendarColor: event.calendarColor,
        bgColorHover: event.bgColorHover,
        parentEventId: exception.parentEventId,
      };
      // Only include instances that overlap with the view range (if provided)
      if (viewStart && viewEnd && overrideInstance.startDate && overrideInstance.endDate) {
        const overrideStart = timezone
          ? dayjs.utc(overrideInstance.startDate).tz(timezone)
          : dayjs(overrideInstance.startDate);
        const overrideEnd = timezone
          ? dayjs.utc(overrideInstance.endDate).tz(timezone)
          : dayjs(overrideInstance.endDate);
        if (overrideEnd.isBefore(viewStart) || overrideStart.isAfter(viewEnd)) {
          // Instance doesn't overlap with view, skip it
          switch (recurrencePattern.frequency) {
            case EVENT_FREQUENCY.DAILY:
              current = current.add(1, 'day');
              break;
            case EVENT_FREQUENCY.WEEKLY:
              current = current.add(1, 'week');
              break;
            case EVENT_FREQUENCY.MONTHLY:
              current = current.add(1, 'month');
              break;
            case EVENT_FREQUENCY.YEARLY:
              current = current.add(1, 'year');
              break;
            default:
              return instances;
          }
          iterationCount++;
          continue;
        }
      }

      instances.push(overrideInstance);
    } else {
      // Generate a normal instance for this date
      // Calculate new start and end dates preserving church-local wall-clock time and duration
      const { startDate: instanceStartDate, endDate: instanceEndDate } = buildOccurrenceUtcRange({
        occurrenceLocalDate: occurrenceDate,
        originalStartLocal: originalStartDate,
        durationMs,
        timezone,
        isAllDay: event.isAllDay,
      });

      // Only include instances that overlap with the view range (if provided)
      if (viewStart && viewEnd) {
        const instanceStart = timezone ? dayjs.utc(instanceStartDate).tz(timezone) : dayjs(instanceStartDate);
        const instanceEnd = timezone ? dayjs.utc(instanceEndDate).tz(timezone) : dayjs(instanceEndDate);
        // Check if instance overlaps with view range
        if (instanceEnd.isBefore(viewStart) || instanceStart.isAfter(viewEnd)) {
          // Instance doesn't overlap with view, skip it
          // Continue to next iteration
          switch (recurrencePattern.frequency) {
            case EVENT_FREQUENCY.DAILY:
              current = current.add(1, 'day');
              break;
            case EVENT_FREQUENCY.WEEKLY:
              current = current.add(1, 'week');
              break;
            case EVENT_FREQUENCY.MONTHLY:
              current = current.add(1, 'month');
              break;
            case EVENT_FREQUENCY.YEARLY:
              current = current.add(1, 'year');
              break;
            default:
              return instances;
          }
          iterationCount++;
          continue;
        }
      }

      // Create a new event instance (expanded occurrence from recurring event)
      const instance: CalendarEventType = {
        ...event,
        isPerpetual: false as const,
        startDate: instanceStartDate,
        endDate: instanceEndDate,
        eventRecurrence: {
          frequency: recurrencePattern.frequency,
          interval: recurrencePattern.interval,
          byDay: recurrencePattern.byDay,
          bySetPos: recurrencePattern.bySetPos ?? null,
          until: recurrencePattern.until,
          count: recurrencePattern.count,
        },
        eventExceptions: [],
        parentEventId: event.id,
      };

      instances.push(instance);
    }

    // Increment based on frequency:
    // - Daily: add 1 day (preserves time)
    // - Weekly: add 1 week (preserves day of week and time)
    // - Monthly: add 1 month (preserves date and time, handles edge cases like Jan 31 -> Feb 28/29)
    // - Yearly: add 1 year (preserves date and time, handles leap years)
    switch (recurrencePattern.frequency) {
      case EVENT_FREQUENCY.DAILY:
        // Generate instance for every day at same time until the repeat end date
        current = current.add(1, 'day');
        break;
      case EVENT_FREQUENCY.WEEKLY:
        // Generate one instance for every next week at the same day of the week
        current = current.add(1, 'week');
        break;
      case EVENT_FREQUENCY.MONTHLY:
        // Generate instance for same date every month
        current = current.add(1, 'month');
        break;
      case EVENT_FREQUENCY.YEARLY:
        // Generate instance on same date of the year
        current = current.add(1, 'year');
        break;
      default:
        return instances;
    }

    iterationCount++;
  }

  return instances;
};
